'use client';

import Icon from '@ant-design/icons';
import { Button, message } from 'antd';
import classnames from 'classnames';
import { useEffect, useState } from 'react';

import GiftSvg from '@/components/Icon/svgs/gift_bring.svg';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import { claimReferralRewards } from '@/service/invite';
import useReferral from '@/store/useReferralStore';
import { ROUTER_PATH, generatePath } from '@/utils/router';
import { copyToClipboard, pluralizeWithCountTName } from '@/utils/utils';

const BringFriends = () => {
  const referralData = useReferral((state) => state.data);

  const [claimLoading, setClaimedLoading] = useState<boolean>(false);

  useEffect(() => {
    useReferral.getState().getRewardDetail();
  }, []);

  const { userProfile } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile
  }));

  const { t } = useTranslation();

  const generateLink = async () => {
    if (!userProfile?.userId) {
      message.error(t('error.oops'));

      return;
    }

    const url = `${window.origin}${generatePath(ROUTER_PATH.invite, {
      inviteCode: userProfile.userId
    })}`;

    copyToClipboard(url).then(() => {
      message.success(
        <span
          dangerouslySetInnerHTML={{
            __html: t('memoryDetail.shareModal.copySuccessTips', {
              url
            })
          }}
        />
      );
    });
  };

  const handleClaim = () => {
    if (!referralData.unclaimRewardNum) return;

    setClaimedLoading(true);

    claimReferralRewards()
      .then((res) => {
        if (res.data.code === 0) {
          message.success('Claim success');
          useReferral.getState().getRewardDetail();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setClaimedLoading(false);
      });
  };

  return (
    <div className="flex flex-col gap-4 mt-7 items-center w-full max-w-[800px] mx-auto">
      <div className="bg-[rgba(254,253,255,0.6)] px-4 py-8 flex flex-col gap-6 items-center w-full rounded-[16px]">
        <img
          alt=""
          className="w-[180px] h-[122px] rounded-[32px]"
          src="https://frontdoorcdn.mindverse.ai/mindos-resource/front-img/crm/202408281724830463345bring.jpg"
        />
        <div className="text-primary-black flex flex-col items-center">
          <div className="font-[Calistoga-Regular] text-[32px] leading-[48px] ">
            {t('menu.bringYourFriends')}
          </div>
          <div className="text-base leading-7 text-[#756C5D] text-center">
            <div>{t('menu.bringYourFriendsModal.titleDiv1')}</div>
            <div>{t('menu.bringYourFriendsModal.titleDiv2')}</div>
          </div>
        </div>

        <div
          className="cursor-pointer bg-[#7B67EF] text-primary-white h-12 px-5 flex text-[18px] leading-8 font-medium items-center justify-center rounded-[40px] transition-colors shadow-[0px_8px_32px_0px_rgba(194,158,107,0.05)] hover:bg-primary-sub-color"
          onClick={() => {
            generateLink();
          }}
        >
          {t('menu.bringYourFriendsModal.inviteFriend')}
        </div>

        <div className="border-0 border-t-[1px] border-solid border-[#fefdff] w-full" />

        <div className="flex flex-col items-center gap-4">
          <div
            className="w-[384px] h-[248px] rounded-[32px] py-[16px] px-[24px] flex flex-col items-center justify-center text-[#160a0a] border border-solid border-[#B8AD9B]"
            style={{ background: 'linear-gradient(180deg, #DECBF1 0%, #F1E8F1 17%, #FEFDFF 100%)' }}
          >
            <Icon className=" text-[24px]" component={GiftSvg} />
            <div className="text-base leading-7">{t('menu.bringYourFriendsModal.yourRewards')}</div>
            <div
              dangerouslySetInnerHTML={{
                __html: t('menu.bringYourFriendsModal.rewardDesc')
              }}
              className="text-sm leading-6 text-center mt-4"
            />
            <Button
              className={classnames(
                'w-full text-[18px] h-12 leading-8 mt-4 flex items-center justify-center rounded-[40px]',
                referralData.unclaimRewardNum
                  ? '!text-primary-white bg-primary-black cursor-pointer duration-200 transition-colors hover:!bg-primary-sub-color'
                  : '!bg-[#EEE9E4] !text-[#ACABB3] cursor-not-allowed'
              )}
              disabled={!referralData.unclaimRewardNum}
              loading={claimLoading}
              onClick={handleClaim}
            >
              {referralData.unclaimRewardNum
                ? t('menu.bringYourFriendsModal.claimRewards', {
                    unclaimedReward: referralData.unclaimRewardNum
                  })
                : t('menu.bringYourFriendsModal.noRewardsYet')}
            </Button>
          </div>
          <div className="text-[16px] leading-7 font-[400] text-[#756C5D]">
            {t(
              pluralizeWithCountTName(
                referralData.claimAccountNum,
                'menu.bringYourFriendsModal.friendRegistered'
              ),
              {
                number: referralData.claimAccountNum
              }
            )}
            {t(
              pluralizeWithCountTName(
                referralData.fullyRegisteredAccountNum,
                'menu.bringYourFriendsModal.friendFinished'
              ),
              {
                number: referralData.fullyRegisteredAccountNum
              }
            )}
            {t(
              pluralizeWithCountTName(
                referralData.activeAccountNum,
                'menu.bringYourFriendsModal.friendActive'
              ),
              {
                number: referralData.activeAccountNum
              }
            )}
            {t(
              pluralizeWithCountTName(
                referralData.claimRewardNum,
                'menu.bringYourFriendsModal.dayClaimed'
              ),
              {
                number: referralData.claimRewardNum
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BringFriends;
